/** Shared formatting helpers for the audit-spend dashboard. */

export function formatUsd(amount: number, fractionDigits = 4): string {
  // 4 digits because per-audit costs are typically in the $0.01–$0.10 range.
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(Number.isFinite(amount) ? amount : 0);
}

export function formatUsd2(amount: number): string {
  return formatUsd(amount, 2);
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(1)} ${units[i]}`;
}

/**
 * An instant in the VIEWER'S timezone (TPF, 2026-09-09), with the zone
 * abbreviation so it is never ambiguous.
 *
 * This used to pin `Europe/Berlin` and was named `formatCet` for it, which was
 * right only for a reader sitting in that zone — everyone else read a wall
 * clock that was not theirs and had no way to tell. `timeZoneName: "short"` is
 * what makes the local rendering self-describing, and it is why the two tables
 * that used to head this column "when (CET)" now just say "when".
 *
 * Distinct from `formatDayShort`/`formatDayLong` below, which take a `YYYY-MM-DD`
 * calendar day rather than an instant and MUST stay in UTC: those strings are
 * BigQuery's own day buckets, and re-interpreting one in a negative-offset zone
 * would move a correct date back a day.
 */
export function formatLocal(isoTimestamp: string | null | undefined): string {
  if (!isoTimestamp) return '—';
  const d = new Date(isoTimestamp);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(d);
}

/**
 * `YYYY-MM-DD` → `2 Aug`. The day strings are UTC calendar dates from
 * BigQuery, so this formats in UTC — parsing them in local time would shift
 * the label a day back for any negative-offset viewer.
 */
export function formatDayShort(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
  }).format(d);
}

/** `YYYY-MM-DD` → `Sat 2 Aug 2026` (UTC), for tooltip headers. */
export function formatDayLong(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

export function formatInt(n: number): string {
  return new Intl.NumberFormat('en-US').format(Number.isFinite(n) ? n : 0);
}

export function formatPct(n: number, fractionDigits = 1): string {
  return `${(n * 100).toFixed(fractionDigits)}%`;
}

export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  if (ms < 1000) return `${ms.toFixed(0)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = s / 60;
  return `${m.toFixed(1)} m`;
}
